import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, runTransaction, Timestamp, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import LoadingSkeleton from '../components/LoadingSkeleton';
import toast from 'react-hot-toast';
// import Select from 'react-select'; // Import kept in case needed later, but removing actual usage for invite
import AddMemberModal from '../components/AddMemberModal.js';
import ConfirmationModal from '../components/ConfirmationModal.js';

function HutDetailsPage() {
    const { hutId } = useParams();
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        isDangerous: false,
        onConfirm: () => { }
    });

    const fetchData = useCallback(async () => {
        try {
            const groupRef = doc(db, 'groups', hutId);
            const groupSnap = await getDoc(groupRef);

            if (!groupSnap.exists()) {
                toast.error("Hut not found");
                navigate('/');
                return;
            }

            setGroup({ id: groupSnap.id, ...groupSnap.data() });

            // Members
            const membersSnap = await getDocs(collection(db, 'groups', hutId, 'members'));
            setMembers(membersSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        } catch (error) {
            console.error("Error loading hut details", error);
            toast.error("Failed to load details");
        } finally {
            setLoading(false);
        }
    }, [hutId, navigate]);

    useEffect(() => {
        if (user) {
            fetchData();
        } else {
            // Let protected route handle redirect, just clear state if it unmounts
            setGroup(null);
            setMembers([]);
            setLoading(false);
        }
    }, [user, fetchData]);

    // Self-Healing: Fix out-of-sync spot counts
    useEffect(() => {
        if (!group || loading || members.length === 0) return;

        const confirmedCount = members.filter(m => m.status === 'confirmed').length;
        const recordedCount = group.spotsTaken || 0;

        if (confirmedCount !== recordedCount) {
            console.log(`Self-healing: correcting spotsTaken from ${recordedCount} to ${confirmedCount}`);
            // Fire and forget update
            const groupRef = doc(db, 'groups', hutId);
            setDoc(groupRef, { spotsTaken: confirmedCount }, { merge: true })
                .then(() => {
                    console.log("Self-healing complete");
                    // Update local state to reflect fix without full reload
                    setGroup(prev => ({ ...prev, spotsTaken: confirmedCount }));
                })
                .catch(err => console.error("Self-healing failed", err));
        }
    }, [group, members, loading, hutId]);

    const executeDeleteGroup = async () => {
        // if (!window.confirm("Delete this hut entirely?")) return; // Handled by modal

        const toastId = toast.loading("Deleting hut...");
        try {
            console.log("Starting delete for group:", hutId);

            // 1. Clean up subcollections first to avoid orphaned data and permission issues
            // (If we delete group first, we might lose access to list members/schedules depending on rules)

            try {
                // Delete sessions
                const sessionsSnapshot = await getDocs(collection(db, 'groups', hutId, 'schedules'));
                const sessionPromises = sessionsSnapshot.docs.map(d => deleteDoc(d.ref));
                await Promise.all(sessionPromises);

                // Delete members
                const membersSnapshot = await getDocs(collection(db, 'groups', hutId, 'members'));
                const myId = user?.uid;

                // Separate self from others
                const otherMembers = membersSnapshot.docs.filter(d => d.id !== myId);
                const myMemberDoc = membersSnapshot.docs.find(d => d.id === myId);

                // 1. Delete ALL others first (while I am still admin)
                const otherMemberPromises = otherMembers.map(d => deleteDoc(d.ref));
                await Promise.all(otherMemberPromises);

                // 2. Delete myself last
                if (myMemberDoc) {
                    await deleteDoc(myMemberDoc.ref);
                }

            } catch (cleanupError) {
                console.warn("Subcollection cleanup had issues (proceeding to delete group):", cleanupError);
                // We continue to delete the group even if cleanup fails partially
            }

            // 2. Delete group doc
            await deleteDoc(doc(db, 'groups', hutId));

            toast.success("Hut deleted", { id: toastId });
            navigate('/');

        } catch (e) {
            console.error("Delete failed:", e);
            toast.error(`Failed to delete: ${e.message}`, { id: toastId });
        }
    };

    const executeLeaveGroup = async () => {
        // if (!window.confirm("Are you sure you want to leave this hut?")) return; // Handled by modal
        const toastId = toast.loading("Leaving hut...");

        try {
            await runTransaction(db, async (transaction) => {
                const groupRef = doc(db, 'groups', hutId);
                const groupDoc = await transaction.get(groupRef);
                if (!groupDoc.exists()) throw new Error("Group not found");

                // const groupData = groupDoc.data(); // Unused

                const userMemberRef = doc(db, 'groups', hutId, 'members', user.uid);
                const userProfileGroupRef = doc(db, 'userProfiles', user.uid, 'groups', hutId);

                // Check if user is actually a member
                const userMemberDoc = await transaction.get(userMemberRef);
                if (!userMemberDoc.exists()) throw new Error("You are not a member");

                transaction.delete(userMemberRef);
                transaction.delete(userProfileGroupRef);
            });

            // Logic to promote waitlisted user after leaving
            const membersSnap = await getDocs(collection(db, 'groups', hutId, 'members'));
            const allMembers = membersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            const myMemberRec = members.find(m => m.userId === user.uid);

            const wasConfirmed = myMemberRec?.status === 'confirmed';

            // Find waitlist candidate
            const waitlisted = allMembers
                .filter(m => m.status === 'waitlist')
                .sort((a, b) => a.joinedAt.toMillis() - b.joinedAt.toMillis());

            const candidate = waitlisted.length > 0 ? waitlisted[0] : null;

            await runTransaction(db, async (transaction) => {
                const groupRef = doc(db, 'groups', hutId);
                const groupDoc = await transaction.get(groupRef);
                if (!groupDoc.exists()) throw new Error("Group not found");

                // If I was confirmed, we need to fill my spot or reduce count
                if (wasConfirmed) {
                    if (candidate) {
                        // Promote candidate
                        const candidateRef = doc(db, 'groups', hutId, 'members', candidate.userId);
                        transaction.update(candidateRef, { status: 'confirmed' });
                    } else {
                        // Reduce count
                        const newCount = (groupDoc.data().spotsTaken || 1) - 1;
                        transaction.update(groupRef, { spotsTaken: Math.max(0, newCount) });
                    }
                }
            });

            toast.success("Left hut successfully", { id: toastId });
            if (candidate && wasConfirmed) {
                console.log(`Promoted user ${candidate.userName}`);
            }
            navigate('/');

        } catch (error) {
            console.error("Leave error", error);
            toast.error(`Failed to leave: ${error.message}`, { id: toastId });
        }
    };

    const handleAddMember = async (name) => {
        const toastId = toast.loading("Adding member...");
        try {
            await runTransaction(db, async (transaction) => {
                const groupRef = doc(db, 'groups', hutId);
                const groupDoc = await transaction.get(groupRef);
                if (!groupDoc.exists()) throw new Error("Group does not exist!");

                const currentData = groupDoc.data();
                const currentCount = currentData.spotsTaken || 0;
                const maxPlayers = currentData.maxPlayers || 4;

                let newStatus = 'waitlist';
                if (currentCount < maxPlayers) {
                    newStatus = 'confirmed';
                    transaction.update(groupRef, { spotsTaken: currentCount + 1 });
                }

                // Create a "ghost" user ID
                const ghostId = `manual_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

                const memberRef = doc(db, 'groups', hutId, 'members', ghostId);
                transaction.set(memberRef, {
                    userId: ghostId,
                    userName: name,
                    joinedAt: Timestamp.now(),
                    role: 'guest',
                    status: newStatus,
                    addedBy: user.uid
                });
            });
            toast.success(`${name} added!`, { id: toastId });
            setShowAddMemberModal(false);
            fetchData(); // Refresh list
        } catch (e) {
            console.error("Add member failed:", e);
            toast.error(`Failed to add: ${e.message}`, { id: toastId });
        }
    };


    const executeRemoveMember = async (memberId, memberName) => {
        // if (!window.confirm(`Remove ${memberName}?`)) return; // Handled by modal
        const toastId = toast.loading("Removing member...");

        try {
            await runTransaction(db, async (transaction) => {
                const groupRef = doc(db, 'groups', hutId);
                const memberRef = doc(db, 'groups', hutId, 'members', memberId);

                const groupDoc = await transaction.get(groupRef);
                const memberDoc = await transaction.get(memberRef);

                if (!groupDoc.exists()) throw new Error("Group not found");
                if (!memberDoc.exists()) throw new Error("Member not found");

                const memberData = memberDoc.data();
                const wasConfirmed = memberData.status === 'confirmed';

                // 1. Delete member
                transaction.delete(memberRef);

                // 2. If valid user, delete their profile link
                // Check if it's a real user (not manual/ghost)
                if (!memberId.startsWith('manual_')) {
                    const userProfileGroupRef = doc(db, 'userProfiles', memberId, 'groups', hutId);
                    transaction.delete(userProfileGroupRef);
                }

                // 3. Update spots
                if (wasConfirmed) {
                    const currentSpots = groupDoc.data().spotsTaken || 0;
                    transaction.update(groupRef, { spotsTaken: Math.max(0, currentSpots - 1) });
                }
            });

            toast.success(`${memberName} removed`, { id: toastId });
            fetchData();

        } catch (error) {
            console.error("Remove failed:", error);
            toast.error(`Failed to remove: ${error.message}`, { id: toastId });
        }
    };




    // Modal Triggers
    const handleDeleteClick = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Hut',
            message: 'Are you sure you want to delete this hut entirely? This action cannot be undone.',
            confirmText: 'Delete Hut',
            isDangerous: true,
            onConfirm: () => {
                executeDeleteGroup();
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleLeaveClick = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Leave Hut',
            message: 'Are you sure you want to leave this hut?',
            confirmText: 'Leave',
            isDangerous: true,
            onConfirm: () => {
                executeLeaveGroup();
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleRemoveMemberClick = (memberId, memberName) => {
        setConfirmModal({
            isOpen: true,
            title: 'Remove Member',
            message: `Are you sure you want to remove ${memberName}?`,
            confirmText: 'Remove',
            isDangerous: true,
            onConfirm: () => {
                executeRemoveMember(memberId, memberName);
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleShareLink = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url).then(() => {
            toast.success("Hut link copied to clipboard!");
        }).catch(() => {
            toast.error("Failed to copy link");
        });
    };

    if (loading) return <div className="p-6"><LoadingSkeleton /></div>;
    if (!group) return null;

    const isAdmin = members.find(m => m.userId === user?.uid)?.role === 'admin';
    const isMember = members.some(m => m.userId === user?.uid);
    const confirmedMembers = members.filter(m => m.status === 'confirmed');
    const waitlistMembers = members.filter(m => m.status === 'waitlist');

    return (
        <div className="max-w-4xl mx-auto p-6">
            <button onClick={() => navigate(-1)} className="mb-4 text-gray-500 hover:text-gray-700">← Back</button>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">{group.name}</h1>
                        <p className="text-gray-600 mb-4">{group.description}</p>
                        <div className="flex gap-6 text-sm text-gray-500">
                            <span>📍 {group.location}</span>
                            <span>📅 {new Date(group.date).toLocaleDateString()} {group.time}</span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleShareLink}
                            className="px-4 py-2 bg-sky-100 text-sky-700 rounded-lg hover:bg-sky-200"
                        >
                            🔗 Share Link
                        </button>
                        {!isAdmin && isMember && (
                            <button
                                onClick={handleLeaveClick}
                                className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
                            >
                                Leave
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                onClick={handleDeleteClick}
                                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                            >
                                Delete
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg text-gray-800">Roster</h3>
                    {isAdmin && (
                        <button
                            onClick={() => setShowAddMemberModal(true)}
                            className="text-sm px-3 py-1 bg-sky-50 text-sky-600 rounded hover:bg-sky-100 border border-sky-200"
                        >
                            + Add Member
                        </button>
                    )}
                </div>

                <div className="space-y-4">
                    <div>
                        <h4 className="text-green-700 font-medium text-sm mb-2">Confirmed ({confirmedMembers.length}/{group.maxPlayers || 4})</h4>
                        {confirmedMembers.map(m => (
                            <div key={m.id} className="p-2 bg-green-50 rounded mb-1 flex justify-between items-center">
                                <span className="font-medium text-gray-700">{m.userName || 'Paddle Player'}</span>
                                <div className="flex gap-2 items-center">
                                    {m.userId === user?.uid && <span className="text-xs text-green-600 font-bold bg-green-100 px-2 py-0.5 rounded-full">YOU</span>}
                                    {isAdmin && m.userId !== user?.uid && (
                                        <button
                                            className="text-gray-400 hover:text-red-500"
                                            title="Remove Member"
                                            onClick={() => handleRemoveMemberClick(m.userId, m.userName)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {confirmedMembers.length === 0 && <span className="text-gray-400 italic text-sm">Empty</span>}
                    </div>
                    <div>
                        <h4 className="text-yellow-700 font-medium text-sm mb-2">Waitlist</h4>
                        {waitlistMembers.map(m => (
                            <div key={m.id} className="p-2 bg-yellow-50 rounded mb-1 flex justify-between items-center">
                                <span className="font-medium text-gray-700">{m.userName || 'Paddle Player'}</span>
                                <div className="flex gap-2 items-center">
                                    {m.userId === user?.uid && <span className="text-xs text-yellow-600 font-bold bg-yellow-100 px-2 py-0.5 rounded-full">YOU</span>}
                                    {isAdmin && m.userId !== user?.uid && (
                                        <button
                                            className="text-gray-400 hover:text-red-500"
                                            title="Remove Member"
                                            onClick={() => handleRemoveMemberClick(m.userId, m.userName)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {waitlistMembers.length === 0 && <span className="text-gray-400 italic text-sm">Empty</span>}
                    </div>
                </div>
            </div>
            {showAddMemberModal && (
                <AddMemberModal
                    onClose={() => setShowAddMemberModal(false)}
                    onSubmit={handleAddMember}
                />
            )}
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText={confirmModal.confirmText}
                isDangerous={confirmModal.isDangerous}
            />
        </div>
    );
}

export default HutDetailsPage;
